<script>
import { onMounted } from "vue";
import { useCourseFetchStore } from "@/stores/course/course-fetch-store.js";
import { useCourseDataStore } from "@/stores/course/course-data-store.js";
import { useSharedStore } from "@/stores/shared-store";
import { useAuthStore } from "@/stores/auth-store";

export default {
  setup() {
    const sharedStore = useSharedStore();
    const authStore = useAuthStore();
    const courseFetchStore = useCourseFetchStore();
    const courseDataStore = useCourseDataStore();

    onMounted(() => {
      courseFetchStore.fetchCourseList();
    });
    return {
      sharedStore,
      authStore,
      courseFetchStore,
      courseDataStore,
    };
  },
  methods: {
    goToPage(page) {
      if (page >= 1 && page <= this.courseFetchStore.totalPages) {
        this.courseFetchStore.pagination.currentPage = page;
      }
    },
    getPageNumbers(current, total) {
      const pages = [];

      if (total <= 5) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else {
        if (current <= 3) {
          pages.push(1, 2, 3, 4, 5);
        } else if (current >= total - 2) {
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(
            current - 2,
            current - 1,
            current,
            current + 1,
            current + 2
          );
        }
      }

      return pages;
    },
    statusBadgeClass(status) {
      const map = {
        Ongoing: "bg-warning",
        Completed: "bg-success",
        Expired: "bg-danger",
        Active: "bg-success",
        New: "bg-warning",
        Disabled: "bg-danger",
      };
      return map[status] || "bg-light";
    },
    clearFilters() {
      this.courseFetchStore.clearAllFilters();
    },
    detailRoute(courseId) {
      return {
        name: "CourseDetail",
        params: { course_id: courseId },
      };
    },
    resolveMediaUrl(path) {
      if (!path) return null;
      if (typeof path !== "string") return null;
      if (path.startsWith("http://") || path.startsWith("https://")) {
        return path;
      }
      const base = (this.authStore.Url || "").replace(/\/$/, "");
      const normalized = path.startsWith("/") ? path : `/${path}`;
      if (normalized.startsWith("/media/")) {
        return `${base}${normalized}`;
      }
      return `${base}/media${normalized}`;
    },
    courseThumbnail(item) {
      return this.resolveMediaUrl(item?.thumbnail);
    },
    instructorPhoto(instructorId) {
      const photo = this.authStore.userPhoto(instructorId);
      if (!photo) return null;
      const base = (this.authStore.authUrl || "").replace(/\/$/, "");
      return `${base}/storage/photo/${photo}`;
    },
  },
};
</script>

<template>
  <layouts-header></layouts-header>

  <!-- Breadcrumb -->
  <div class="breadcrumb-bar text-center">
    <div class="container">
      <div class="row">
        <div class="col-md-12 col-12">
          <h2 class="breadcrumb-title mb-2">Course</h2>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb justify-content-center mb-0">
              <li class="breadcrumb-item">
                <router-link to="/home">Home</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">Course</li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <!-- /Breadcrumb -->

  <!-- Course -->
  <section class="course-content course-list-content">
    <div class="container">
      <div class="row align-items-baseline">
        <course-sidebar></course-sidebar>
        <div class="col-lg-9">
          <!-- Filter toolbar -->
          <div class="showing-list mb-4">
            <div class="row align-items-center g-3">
              <div class="col-lg-5">
                <div class="show-result text-center text-lg-start">
                  <h6 class="fw-medium mb-0">
                    <template v-if="courseFetchStore.filteredCount === 0">
                      No courses found
                    </template>
                    <template v-else>
                      Showing
                      {{ courseFetchStore.paginatedCourses.length }}
                      of
                      {{ courseFetchStore.filteredCount }}
                      courses
                      <span
                        v-if="courseFetchStore.totalPages > 1"
                        class="text-muted fw-normal"
                      >
                        · Page
                        {{ courseFetchStore.pagination.currentPage }}
                        of
                        {{ courseFetchStore.totalPages }}
                      </span>
                    </template>
                  </h6>
                </div>
              </div>
              <div class="col-lg-7">
                <div class="show-filter add-course-info">
                  <form action="#" @submit.prevent>
                    <div
                      class="d-sm-flex justify-content-center justify-content-lg-end align-items-center gap-2 mb-1 mb-lg-0"
                    >
                      <div
                        class="view-icons mb-2 mb-sm-0"
                        role="group"
                        aria-label="Course view mode"
                      >
                        <router-link
                          to="/course/course-grid"
                          class="grid-view"
                          aria-label="Grid view"
                        >
                          <i class="feather-grid" aria-hidden="true"></i>
                        </router-link>
                        <router-link
                          to="/course/course-list"
                          class="list-view active"
                          aria-label="List view"
                          aria-current="page"
                        >
                          <i class="isax isax-task" aria-hidden="true"></i>
                        </router-link>
                      </div>
                      <div class="search-group flex-grow-1">
                        <label class="visually-hidden" for="course-list-search">
                          Search courses
                        </label>
                        <i
                          class="isax isax-search-normal-1"
                          aria-hidden="true"
                        ></i>
                        <input
                          id="course-list-search"
                          type="search"
                          class="form-control"
                          placeholder="Search courses"
                          autocomplete="off"
                          :value="courseFetchStore.searchKeyword"
                          @input="
                            courseFetchStore.setSearchKeyword(
                              $event.target.value
                            )
                          "
                        />
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          <!-- /Filter toolbar -->

          <div class="row course-list-wrap">
            <div
              v-if="courseFetchStore.paginatedCourses.length === 0"
              class="col-12"
            >
              <div class="text-center py-5 px-3">
                <span
                  class="icon-box bg-secondary-transparent d-inline-flex align-items-center justify-content-center mb-3"
                >
                  <i class="isax isax-search-status-1 fs-24" aria-hidden="true"></i>
                </span>
                <h6 class="fw-bold mb-1">
                  {{
                    courseFetchStore.hasActiveFilters
                      ? "No courses match your filters"
                      : "No courses available"
                  }}
                </h6>
                <p class="text-muted fs-14 mb-3">
                  {{
                    courseFetchStore.hasActiveFilters
                      ? "Try another keyword, or clear filters to see all courses."
                      : "Check back later for newly published courses."
                  }}
                </p>
                <button
                  v-if="courseFetchStore.hasActiveFilters"
                  type="button"
                  class="btn btn-sm btn-secondary"
                  @click="clearFilters"
                >
                  Clear filters
                </button>
              </div>
            </div>

            <div
              class="col-12"
              v-for="(item, index) in courseFetchStore.paginatedCourses"
              :key="item.course_id || index"
            >
              <div class="courses-list-item">
                <div class="d-md-flex align-items-center">
                  <div
                    class="position-relative overflow-hidden rounded-3 card-image flex-shrink-0"
                  >
                    <router-link :to="detailRoute(item.course_id)">
                      <img
                        v-if="courseThumbnail(item)"
                        class="img-fluid rounded-3 object-fit-cover w-100"
                        :src="courseThumbnail(item)"
                        :alt="item.title || 'Course thumbnail'"
                      />
                      <div
                        v-else
                        class="d-flex align-items-center justify-content-center bg-light rounded-3 w-100"
                        style="height: 165px"
                      >
                        <i class="isax isax-gallery text-muted fs-24"></i>
                      </div>
                    </router-link>
                  </div>
                  <div
                    class="course-list-contents w-100 min-w-0 ps-0 ps-md-3 pt-4 pt-md-0"
                  >
                    <div
                      class="d-flex flex-wrap align-items-center justify-content-between gap-2"
                    >
                      <div class="d-flex align-items-center min-w-0">
                        <div class="avatar avatar-sm rounded-circle flex-shrink-0">
                          <img
                            v-if="instructorPhoto(item.instructor)"
                            class="img-fluid rounded-circle"
                            :src="instructorPhoto(item.instructor)"
                            :alt="
                              authStore.userName(item.instructor) ||
                              'Instructor'
                            "
                          />
                          <span
                            v-else
                            class="avatar-title rounded-circle bg-light text-muted"
                          >
                            <i class="isax isax-user"></i>
                          </span>
                        </div>
                        <p
                          class="ms-2 mb-0 text-truncate"
                          :title="authStore.userName(item.instructor)"
                        >
                          {{ authStore.userName(item.instructor) }}
                        </p>
                      </div>
                      <span
                        v-if="item.status"
                        class="badge badge-light rounded-pill d-inline-flex align-items-center fs-13 fw-medium mb-0 flex-shrink-0 text-nowrap"
                        :class="statusBadgeClass(item.status)"
                      >
                        {{ item.status }}
                      </span>
                    </div>
                    <h4 class="mt-3 mb-2 text-truncate">
                      <router-link
                        :to="detailRoute(item.course_id)"
                        :title="item.title"
                      >
                        {{ item.title }}
                      </router-link>
                    </h4>
                    <div class="d-flex align-items-center">
                      <span class="dot" aria-hidden="true"></span>
                      <p class="mb-0 text-truncate" :title="item.level">
                        {{ item.level || "—" }}
                      </p>
                    </div>
                    <div
                      class="d-flex justify-content-end mt-3 align-items-center"
                    >
                      <router-link
                        :to="detailRoute(item.course_id)"
                        class="btn btn-dark btn-sm d-inline-flex align-items-center text-nowrap"
                      >
                        View Course
                        <i
                          class="isax isax-arrow-right-3 ms-1"
                          aria-hidden="true"
                        ></i>
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <nav
            v-if="courseFetchStore.totalPages > 1"
            class="row align-items-center mt-2"
            aria-label="Course pagination"
          >
            <div class="col-md-12">
              <ul
                class="pagination lms-page justify-content-center justify-content-md-end mt-2 mt-md-0 mb-0"
              >
                <li
                  class="page-item prev"
                  :class="{
                    disabled: courseFetchStore.pagination.currentPage === 1,
                  }"
                >
                  <a
                    class="page-link"
                    href="javascript:void(0)"
                    tabindex="-1"
                    aria-label="Previous page"
                    @click="
                      goToPage(courseFetchStore.pagination.currentPage - 1)
                    "
                  >
                    <i class="fas fa-angle-left" aria-hidden="true"></i>
                  </a>
                </li>

                <li
                  v-if="
                    courseFetchStore.totalPages > 5 &&
                    courseFetchStore.pagination.currentPage > 3
                  "
                  class="page-item"
                >
                  <a
                    class="page-link"
                    href="javascript:void(0)"
                    @click="goToPage(1)"
                    >1</a
                  >
                </li>
                <li
                  v-if="
                    courseFetchStore.totalPages > 5 &&
                    courseFetchStore.pagination.currentPage > 4
                  "
                  class="page-item disabled"
                >
                  <span class="page-link">...</span>
                </li>

                <li
                  v-for="page in getPageNumbers(
                    courseFetchStore.pagination.currentPage,
                    courseFetchStore.totalPages
                  )"
                  :key="page"
                  class="page-item"
                  :class="{
                    active: courseFetchStore.pagination.currentPage === page,
                  }"
                >
                  <a
                    class="page-link"
                    href="javascript:void(0)"
                    :aria-label="`Page ${page}`"
                    :aria-current="
                      courseFetchStore.pagination.currentPage === page
                        ? 'page'
                        : undefined
                    "
                    @click="goToPage(page)"
                    >{{ page }}</a
                  >
                </li>

                <li
                  v-if="
                    courseFetchStore.totalPages > 5 &&
                    courseFetchStore.pagination.currentPage <
                      courseFetchStore.totalPages - 3
                  "
                  class="page-item disabled"
                >
                  <span class="page-link">...</span>
                </li>
                <li
                  v-if="
                    courseFetchStore.totalPages > 5 &&
                    courseFetchStore.pagination.currentPage <
                      courseFetchStore.totalPages - 2
                  "
                  class="page-item"
                >
                  <a
                    class="page-link"
                    href="javascript:void(0)"
                    @click="goToPage(courseFetchStore.totalPages)"
                  >
                    {{ courseFetchStore.totalPages }}
                  </a>
                </li>

                <li
                  class="page-item next"
                  :class="{
                    disabled:
                      courseFetchStore.pagination.currentPage ===
                      courseFetchStore.totalPages,
                  }"
                >
                  <a
                    class="page-link"
                    href="javascript:void(0)"
                    aria-label="Next page"
                    @click="
                      goToPage(courseFetchStore.pagination.currentPage + 1)
                    "
                  >
                    <i class="fas fa-angle-right" aria-hidden="true"></i>
                  </a>
                </li>
              </ul>
            </div>
          </nav>
          <!-- /Pagination -->
        </div>
      </div>
    </div>
  </section>
  <!-- /Course -->
</template>
